import React, { useContext, useMemo, useCallback } from 'react';
import { Checkbox, Col, Divider, message, Modal, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import useTradeStore from '../../hooks/trade/tradeStore';
import BigNumber from 'bignumber.js';
import { useTradeApi } from '../../hooks/trade/tradeApi'; // ✅ Context 기반 API Hook 사용
import { useOrderBook } from '../../hooks/trade/orderBook';
import { useTradePosition } from '../../hooks/trade/tradePosition';
import { useTradeSetting } from '../../hooks/trade/tradeSetting';
import { useTradeModalStore } from '../../hooks/trade/modalStore';
import { useIndexPrice } from '../../hooks/trade/indexprice';
import { ThemeContext } from '@/theme/ThemeProvider.tsx';
import { useDisableScroll } from '@utils/useDisableScroll.tsx';
import { formatDecimal } from '@utils/formatDecimal';

import { useCiTradeWebSocketStore } from '../../hooks/ciTradeWebsocketStore.ts';
import usePercentageStore from '@/hooks/trade/percentageStore';
import { USER_LOG_TYPE, useUserLogger } from '@/app/hooks/userLogger';
interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  long_short: string;
  orderId: number | null;
  rawOrderPrice: number;
  orderItem: any;
}

const EditCheckPositionModal = ({ open, setOpen, long_short, orderId, rawOrderPrice, orderItem }: IProps) => {
  const { t } = useTranslation();
  const { updateOrder } = useTradeApi(); // ✅ Demo/Real 자동 선택
  const { data: tradeStore } = useTradeStore();
  const { textw, raise, fail, borderColor, textGrey } = useContext(ThemeContext).theme.COLORS;

  const { getPrice } = useIndexPrice(tradeStore?.ep_id);
  const { data: tradeModalStore, mutate: mutateTradeModalStore } =
    useTradeModalStore();
  const { data: tradeSetting } = useTradeSetting(tradeStore?.ep_id);
  const { mutate: mutateOrderBook } = useOrderBook(tradeStore?.ep_id);
  const { mutateUserLog, getOrderLog } = useUserLogger();
  const { mutate: mutatePosition } = useTradePosition();
  const main = tradeStore?.pairs?.split('/')[0];
  const currency = tradeStore?.pairs?.split('/')[1];

  //const tradeData = useBinanceWebSocketStore((s) => s.tradeData);
  const globalPercentage = usePercentageStore((state) => state.percentage);

  const currentMarketPrice = useCiTradeWebSocketStore((s) => s.tradePrice);

  // 모달이 열려 있을 때만 가격 계산 (useMemo로 캐싱하여 불필요한 재계산 방지)
  const displayMarketPrice = useMemo(() => {
    if (!open) return 0;
    return getPrice(tradeStore?.ep_id) || 0;
  }, [open, getPrice, tradeStore?.ep_id]);

  const sendOrder = async () => {

    if (!orderItem?.amount)
      return message.error(t('pleaseFillAllFields') + '');

    // if (tradeStore?.tpsl) {
    //   if (isNaN(tradeStore?.tp) || isNaN(tradeStore?.sl)) {
    //     return message.error('CHECK TP/SL');
    //   }
    // }


    
    // 지정가 주문 여부 확인
    const isLimitOrder =
      orderItem?.limit_market?.toUpperCase() == 'L' ||
      orderItem?.limit_market?.toUpperCase() == 'LIMIT';
    
    // 최종 주문 타입 결정
    let finalOrderType = isLimitOrder ? 'L' : 'M';
    
    if (isLimitOrder) {
      // 주문 가격과 현재 가격 비교
      const orderPrice = Number(rawOrderPrice);
      
      // 불리한 가격인지 확인
      const isPriceUnfavorable = 
        (long_short === 'L' && Number(orderPrice) > Number(currentMarketPrice)) || // 매수인데 지정가가 시장가보다 높음
        (long_short === 'S' && Number(orderPrice) < Number(currentMarketPrice));   // 매도인데 지정가가 시장가보다 낮음
      
      // 불리한 가격이면 시장가로 전환
      if (isPriceUnfavorable) {
        finalOrderType = 'M';
        // message.warning('불리한 가격으로 시장가 전환됨');
      }
    }


    if (!orderId) {
      message.error(t('주문 ID가 없습니다'));
      return;
    }

    const orderParams = {
      order_id: orderId,
      price: rawOrderPrice
    }

    const result = await updateOrder(orderParams); // ✅ Context 기반 API 호출
    //mutateStore({ ...tradeStore, order_amount: 0 })

    if (result.result.success) {
      // mutateUserLog({
      //   log : getOrderLog(orderParams, USER_LOG_TYPE.ORDER_OPEN),
      //   data : orderParams,
      // })
      mutateOrderBook();
      mutatePosition();

      setTimeout(() => {
        mutateOrderBook();
        mutatePosition(null);
      }, 500);

      message.success(t('ordersuccess'));
    } else {
      if (result?.result?.message == 'ERROR_MAXAMOUNT') {
        message.error(
          t('ERROR_MAXAMOUNT') +
            ' / MAX AMOUNT : ' +
            BigNumber(result?.result?.max).toFormat(3)
        );
      } else {
        message.error(t(result?.result?.message + ''));
      }
    }
  };

  let margin = new BigNumber(orderItem?.amount * orderItem?.leverage)
    .multipliedBy(
      orderItem?.limit_market === 'M'
        ? currentMarketPrice ?? 0
        : rawOrderPrice ?? 0
    )
    .dividedBy(orderItem?.leverage)
    .toFormat(4);

  if (tradeStore?.main_sub == 'S') {
    margin = new BigNumber(orderItem?.amount * orderItem?.leverage)
      .dividedBy(orderItem?.leverage)
      .toFormat(3);
  }

  useDisableScroll(open);

  const handleTouchMove = (e) => {
    e.stopPropagation();
  };

  const longShortColor = long_short === 'L' ? raise : fail;
  const longShortText = long_short === 'L' ? t('long') : t('short');

  return (
    <Modal
      className={'custom-modal'}
      open={open}
      title={t('ordercheck') + ''}
      onCancel={() => {
        setOpen(false);
      }}
      onOk={() => {
        sendOrder();
        setOpen(false);
      }}
      cancelText={t('cancel')}
      okText={t('confirm')}
    >
      <div onTouchMove={handleTouchMove}>
        <Divider style={{ margin: '12px 0' }} />

        {/* 거래 코인 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('pairs')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
              {tradeStore?.pairs}
            </span>
          </Col>
        </Row>

        {/* 가격 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('price')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
              {orderItem?.limit_market === 'M'
                              ? t('market') + " ("+ formatDecimal(displayMarketPrice, tradeSetting?.dp || 2)+")"
                : formatDecimal(rawOrderPrice, tradeSetting?.dp || 2)}
            </span>
          </Col>
        </Row>

        {/* 수량 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('amount')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
              {formatDecimal(
                new BigNumber(orderItem?.amount || 0)
                  .multipliedBy(orderItem?.leverage || 1)
                  .toNumber(),
                8
              )}{' '}
              <span style={{ color: textGrey, fontSize: '13px' }}>
                {orderItem?.main_sub == 'S' ? currency : main}
              </span>
            </span>
          </Col>
        </Row>

        {/* 롱/숏 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('filledType')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{
              fontWeight: 700,
              fontSize: '14px',
              color: longShortColor,
              padding: '4px 12px',
              borderRadius: '4px',
              backgroundColor: `${longShortColor}15`,
              display: 'inline-block'
            }}>
              {longShortText}
            </span>
          </Col>
        </Row>

        {/* 레버리지 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('leverage')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
              {t(orderItem?.cross_iso == 'C' ? 'cross' : 'iso')}{' '}
              <span style={{ color: longShortColor, fontWeight: 700 }}>
                {orderItem?.leverage}X
              </span>
            </span>
          </Col>
        </Row>

        {/* 증거금 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('margin')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
              {margin}{' '}
              <span style={{ color: textGrey, fontSize: '13px' }}>{currency}</span>
            </span>
          </Col>
        </Row>

        {/* 포지션 감소 */}
        {orderItem?.reduce_position !== undefined && (
          <Row style={{
            borderBottom: `1px solid ${borderColor}`,
            padding: '10px 0',
            alignItems: 'center'
          }}>
            <Col span={8}>
              <span style={{ color: textGrey, fontSize: '13px' }}>
                {t('reduceOnly') || '포지션 감소'}
              </span>
            </Col>
            <Col span={16} style={{ textAlign: 'right' }}>
              <span style={{
                fontWeight: 600,
                fontSize: '14px',
                color: orderItem?.reduce_position ? raise : textGrey,
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: orderItem?.reduce_position ? `${raise}15` : 'transparent',
                display: 'inline-block'
              }}>
                {orderItem?.reduce_position ? '예' : '아니오'}
              </span>
            </Col>
          </Row>
        )}

        <Checkbox
          style={{ marginTop: 16, color: textw }}
          checked={tradeModalStore?.skip}
          onChange={(e) => {
            mutateTradeModalStore({
              ...tradeModalStore,
              skip: e.target.checked,
            });
          }}
        >
          {t('notagain')}
        </Checkbox>
      </div>
    </Modal>
  );
};

export default React.memo(EditCheckPositionModal);
